import { Fragment, useState } from 'react';
import { useAsync } from 'react-async';
import { useHistory } from 'react-router';
import { getBillingSpecialists, getDivision, getDivisionBillingSpecialists, updateDivision } from '../../../api/services/divisionService';
import { ResponseStatus } from '../../../components/notifications';
import { FieldInput } from '../../../components/fieldInput';
import Layout from '../../../components/layout';
import SubmitButton from '../../../components/submitButton';
import { useParams } from 'react-router-dom';
import Division from '../../../api/models/division';
import DivisionBillingSpecialist from '../../../api/models/divisionBillingSpecialist';
import User from '../../../api/models/user';
import { selectOption } from '../../../types/selectOption';
import SelectMulti from 'react-select';
import DivisionRequest from '../../../api/requests/divisionRequest';

//GET params
type Params = {
  id: string;
};

const fetchDivision = async ({ id }: any) => {
  return await getDivision(id);
};

const fetchBillingSpecialists = async () => {
  return await getBillingSpecialists();
};

const fetchDivisionBillingSpecialists = async ({ id }: any) => {
  return await getDivisionBillingSpecialists(id);
};

const EditDivision = () => {
  const history = useHistory();

  // State
  const { id } = useParams<Params>();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [billingSpecialists, setBillingSpecialists] = useState<User[]>([]);
  const [billingSpecialistsOptions, setBillingSpecialistsOptions] = useState<selectOption[]>([]);
  const [selectedDivisionBillingSpecialists, setSelectedDivisionBillingSpecialists] = useState<number[]>([]);
  const [selectedDivisionBillingSpecialistsOptions, setSelectedDivisionBillingSpecialistsOptions] = useState<any[]>([]);

  /**
   * Get the data for this page
   */
  const divisionData = useAsync({
    promiseFn: fetchDivision,
    id,
    onResolve: (r) => {
      const d: Division = r.data.division;
      setName(d.name ?? '');
      setDescription(d.description ?? '');
    },
  });

  const billingSpecialistsData = useAsync({
    promiseFn: fetchBillingSpecialists,
    id,
    onResolve: (r) => {
      const b: User[] = r.data;
      const options: selectOption[] = [];
      setBillingSpecialists(b);

      //Loop through the disciplines to generate the options for the multi select
      (b.map((user) => (
        options.push({ value: user.userId ?? 0, label: `${user.lastName}, ${user.firstName}` })
      )));
      options.sort((a, b) => {
        return a.label.localeCompare(b.label);
      });
      setBillingSpecialistsOptions(options);
    },
  });

  const divisionBillingSpecialistsData = useAsync({
    promiseFn: fetchDivisionBillingSpecialists,
    id,
    onResolve: (r) => {
      const b: DivisionBillingSpecialist[] = r.data;
      const options: any[] = [];
      const ids: number[] = [];

      //Loop through the disciplines to generate the options for the multi select
      b.map((user) => options.push({ value: user.userId ?? 0, label: `${user.user?.lastName}, ${user.user?.firstName}` }));
      setSelectedDivisionBillingSpecialistsOptions(options);

      //Generate our specialists array
      b.map((user) => ids.push(user.userId));
      setSelectedDivisionBillingSpecialists(ids);
    },
  });

  /**
   * Logic to save division
   */
  const saveDivision = async () => {
    const request: DivisionRequest = {
      name: name,
      description: description,
      isVisible: true,
      billingSpecialistIds: selectedDivisionBillingSpecialists
    };

    return await updateDivision(Number(id), request);
  };
  const save = useAsync({
    deferFn: saveDivision,
    onResolve: (r) => {
      history.push(`/admin/divisions/`, { message: `"${name}" saved successfully!` })
    },
  });

  const handleDivisionBillingSpecialistsChange = (options: any) => {
    const newBs: number[] = [];
    const ops: selectOption[] = [];

    (options.map((opt: any) => {
      const bs = billingSpecialists.filter((bs) => opt.value === bs.userId)[0];
      newBs.push(bs.userId!);
      ops.push({ value: opt.value, label: opt.label });
      return true;
    }));

    setSelectedDivisionBillingSpecialists(newBs);
    setSelectedDivisionBillingSpecialistsOptions(ops);
  }

  return (
    <Fragment>
      <Layout isLoading={
        divisionData.isPending ||
        save.isPending ||
        divisionBillingSpecialistsData.isPending ||
        billingSpecialistsData.isPending
      }>
        <Fragment>
          <ResponseStatus state={save} />
          <div>
            <div className='grid grid-cols-6 gap-6 mb-6'>
              <div className='col-span-6 sm:col-span-6'>
                <FieldInput
                  name='name'
                  label='Name'
                  minLength={1}
                  maxLength={150}
                  onChange={(e) => setName(e.target.value)}
                  value={name}
                />
              </div>
              <div className='col-span-6 sm:col-span-6'>
                <div className='text-sm'>
                  <label className='primary'>Description<span className="text-red-500 pl-1">*</span></label>
                </div>
                <textarea
                  name='description'
                  minLength={1}
                  onChange={(e) => setDescription(e.target.value)}
                  value={description}
                />
              </div>
            </div>
            <div className='w-full'>
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <label className='primary'>Billing Specialists</label>
                <SelectMulti
                  id="divisionBillingSpecialistsOptions"
                  className='block primary'
                  isMulti={true}
                  key={`dbs-multi-${selectedDivisionBillingSpecialistsOptions.length}`}
                  options={billingSpecialistsOptions}
                  isClearable={true}
                  defaultValue={selectedDivisionBillingSpecialistsOptions}
                  onChange={(e) => handleDivisionBillingSpecialistsChange(e)}
                />
              </div>
            </div>
          </div>

          <hr className='py-4' />

          <div className='text-right'>
            <SubmitButton
              text='Save Changes'
              disabled={name.length === 0 || description?.length === 0}
              spinning={save.isPending}
              onClick={() => { save.run() }}
            />
          </div>
        </Fragment>
      </Layout>
    </Fragment>
  );
};

export default EditDivision;
